import { ReactElement } from "react";
import { FormattedMessage } from "@/i18n/i18n-client";
import { DefaultTextPrice } from "../../components/Price";
import {
  CartError,
  ProductType,
  CurrencyCode,
  CartSimpleItem,
  CartCustomItem,
  CheckoutServiceErrorCodes as ErrorCodes,
  CartFragment,
  CustomerServiceErrorCodes,
  StoreAuthServiceErrorCodes,
} from "../../generated/graphql";

export type FormattedError = CartError & {
  message: ReactElement;
};

export function generateItemRelatedErrors(
  item: CartFragment["items"][0],
  currencyCode: CurrencyCode,
  errors?: CartError[] | null
): FormattedError[] {
  if (!errors?.length) return [];
  const isItemSimple = item.product?.type === ProductType.Simple;
  const { itemRelatedErrors, customItemVariantsTitles } = findItemRelatedErrors(
    item,
    isItemSimple,
    errors
  );

  const formattedErrors = itemRelatedErrors.map((error) => ({
    code: error.code,
    params: error.params,
    message: generateErrorMessage(
      error,
      currencyCode,
      customItemVariantsTitles.get(error.params?.resourceId)
    ),
  }));

  return formattedErrors;
}

function findItemRelatedErrors(
  item: CartFragment["items"][0],
  isItemSimple: boolean,
  errors?: CartError[] | null
) {
  let itemId = "";
  const customItemVariantsTitles = new Map();
  const itemRelatedErrors = (errors || []).filter((error) => {
    const resourceId = error.params?.resourceId;
    if (isItemSimple) {
      const typedSimpleItem = item as CartSimpleItem;
      const productId = typedSimpleItem.product?.id.split("_")[1];
      const variantId = typedSimpleItem.variant?.id.split("_")[1];
      const cartItemId = typedSimpleItem.id.split("_")[1];
      itemId =
        productId && resourceId === productId
          ? productId
          : variantId && resourceId === variantId
            ? variantId
            : cartItemId === resourceId
              ? cartItemId
              : "";
    } else {
      const typedCustomItem = item as CartCustomItem;
      typedCustomItem.categories.map(({ selectedVariants }) => {
        selectedVariants.map(({ variant, id }) => {
          if (
            variant?.id?.split("_")[1] === resourceId ||
            id?.split("_")[1] === resourceId
          ) {
            itemId = variant?.id?.split("_")[1] || id?.split("_")[1];
            customItemVariantsTitles.set(resourceId, variant?.product?.title);
          }
        });
      });
    }
    return itemId === resourceId;
  });
  return { itemRelatedErrors, customItemVariantsTitles };
}

export const isProductNotAvailableError = (
  errors?: CartError[] | FormattedError[] | null
): boolean => {
  if (!errors?.length) return false;
  return !!errors?.find(
    (error) =>
      error.code === ErrorCodes.ProductNotAvailable ||
      (error.code === ErrorCodes.InsufficientQuantity &&
        error?.params?.availableQuantity === 0)
  );
};

export const isCartDirty = (errors?: CartError[] | null): boolean => {
  if (!errors?.length) return false;
  const cartItemsRelatedErrors = errors?.filter(
    (error) =>
      error.code === ErrorCodes.ProductNotAvailable ||
      error.code === ErrorCodes.MaxQuantityExceeded ||
      error.code === ErrorCodes.MinQuantityNotMet ||
      error.code === ErrorCodes.InsufficientQuantity
  );
  return !!cartItemsRelatedErrors?.length;
};

export const isContactInformationError = (
  errors?: CartError[] | null
): boolean => {
  if (!errors?.length) return false;
  const contactInformationRelatedErrors = errors?.filter(
    (error) =>
      error.code === ErrorCodes.InvalidCustomerName ||
      error.code === ErrorCodes.InvalidCustomerIdentifier
  );
  return !!contactInformationRelatedErrors?.length;
};

export const isShippingInformationError = (
  errors?: CartError[] | null
): boolean => {
  if (!errors?.length) return false;
  const shippingInformationRelatedErrors = errors?.filter(
    (error) =>
      error.code === ErrorCodes.InvalidSecondaryPhone ||
      error.code === ErrorCodes.InvalidCheckoutNotes ||
      error.code === ErrorCodes.InvalidCheckoutPostcode
  );
  return !!shippingInformationRelatedErrors?.length;
};

export const isDeliveryError = (errors?: CartError[] | null): boolean => {
  if (!errors?.length) return false;
  const deliveryRelatedErrors = !!errors?.find(
    (error) => error.code === ErrorCodes.InvalidShippingRate
  );
  return deliveryRelatedErrors;
};
export const isCouponError = (errors?: CartError[] | null): boolean => {
  if (!errors?.length) return false;
  const couponRelatedErrors = !!errors?.find(
    (error) => error.code === ErrorCodes.PromoCodeNotValid
  );
  return couponRelatedErrors;
};

function generateErrorMessage(
  error: CartError,
  currencyCode: CurrencyCode,
  customItemVariantTitle?: string
): ReactElement {
  if (error.code === ErrorCodes.ProductNotAvailable) {
    return (
      <FormattedMessage
        defaultMessage="This product is no longer available, <b>please remove it from your cart</b>"
        id="bQGS7N"
        values={{ b: (...chunks) => <b>{chunks}</b> }}
      />
    );
  }
  if (error.code === ErrorCodes.MaxQuantityExceeded) {
    return (
      <FormattedMessage
        defaultMessage="Please edit the quantity of this product, you can only add up to <b>{availableQuantity} per order</b>"
        id="/UgIV+"
        values={{
          availableQuantity: error.params?.availableQuantity,
          b: (...chunks) => <b>{chunks}</b>,
        }}
      />
    );
  }
  if (error.code === ErrorCodes.MinQuantityNotMet) {
    return (
      <FormattedMessage
        defaultMessage="Please edit the quantity of this product, the minimum quantity <b>{availableQuantity} per order</b>"
        id="7ee+8G"
        values={{
          availableQuantity: error.params?.availableQuantity,
          b: (...chunks) => <b>{chunks}</b>,
        }}
      />
    );
  }
  if (error.code === ErrorCodes.InsufficientQuantity) {
    if (customItemVariantTitle)
      return (
        <FormattedMessage
          defaultMessage="Please edit the quantity of {variantTitle} selected variant, <b>available stock: {availableQuantity}</b>"
          id="UqZi9s"
          values={{
            availableQuantity: error.params?.availableQuantity,
            b: (...chunks) => <b>{chunks}</b>,
            variantTitle: customItemVariantTitle,
          }}
        />
      );
    return (
      <FormattedMessage
        defaultMessage="Please edit the quantity of this product, <b>available stock: {availableQuantity}</b>"
        id="zZgyh5"
        values={{
          availableQuantity: error.params?.availableQuantity,
          b: (...chunks) => <b>{chunks}</b>,
        }}
      />
    );
  }
  if (error.code === ErrorCodes.PriceChanged) {
    if (customItemVariantTitle)
      return (
        <FormattedMessage
          defaultMessage="Please note that the price of {variantTitle} selected variant has changed from <b>{requestedPrice} to {availablePrice}</b> since you placed it in your cart"
          id="QBKRdv"
          values={{
            requestedPrice: (
              <DefaultTextPrice
                money={{ amount: error.params?.requestedPrice!, currencyCode }}
              />
            ),
            availablePrice: (
              <DefaultTextPrice
                money={{ amount: error.params?.availablePrice!, currencyCode }}
              />
            ),
            b: (...chunks) => <b>{chunks}</b>,
            variantTitle: customItemVariantTitle,
          }}
        />
      );
    return (
      <FormattedMessage
        defaultMessage="Please note that the price of this product has changed from <b>{requestedPrice} to {availablePrice}</b> since you placed it in your cart"
        id="hy34xE"
        values={{
          requestedPrice: (
            <DefaultTextPrice
              money={{ amount: error.params?.requestedPrice!, currencyCode }}
            />
          ),
          availablePrice: (
            <DefaultTextPrice
              money={{ amount: error.params?.availablePrice!, currencyCode }}
            />
          ),
          b: (...chunks) => <b>{chunks}</b>,
        }}
      />
    );
  }
  return (
    <FormattedMessage
      defaultMessage="Oops, An Error occurred. please try again"
      id="rPijV8"
    />
  );
}

export function getCustomerErrorMessageString(errorCode: any, intl: any) {
  switch (errorCode) {
    case CustomerServiceErrorCodes.DuplicateEmail:
      return intl.formatMessage({
        defaultMessage: "This email address is already in use",
        id: "ebUuoY",
      });
    case CustomerServiceErrorCodes.DuplicatePhone:
      return intl.formatMessage({
        defaultMessage: "This phone number is already in use",
        id: "qSddiR",
      });
    case CustomerServiceErrorCodes.EmailNotVerified:
      return intl.formatMessage({
        defaultMessage: "Email address is not verified",
        id: "D2RW0S",
      });
    case CustomerServiceErrorCodes.EmailTaken:
      return intl.formatMessage({
        defaultMessage: "This email address is already taken",
        id: "6BqQUf",
      });
    case CustomerServiceErrorCodes.InternalServerError:
      return intl.formatMessage({
        defaultMessage: "Internal server error. Please try again later",
        id: "XxYI2Z",
      });
    case CustomerServiceErrorCodes.InvalidEmail:
      return intl.formatMessage({
        defaultMessage: "Please enter a valid email address",
        id: "oLpv29",
      });
    case CustomerServiceErrorCodes.InvalidInput:
      return intl.formatMessage({
        defaultMessage: "Invalid input provided",
        id: "n6d3tX",
      });
    case CustomerServiceErrorCodes.InvalidPhone:
      return intl.formatMessage({
        defaultMessage: "Please enter a valid phone number",
        id: "FPcbu0",
      });
    case CustomerServiceErrorCodes.PhoneAndEmailNotVerified:
      return intl.formatMessage({
        defaultMessage:
          "Both phone number and email address need to be verified",
        id: "l1I7GS",
      });
    case CustomerServiceErrorCodes.PhoneNotVerified:
      return intl.formatMessage({
        defaultMessage: "Phone number is not verified",
        id: "rNOShB",
      });
    case CustomerServiceErrorCodes.RateLimitExceeded:
      return intl.formatMessage({
        defaultMessage: "Rate limit exceeded. Please try again later",
        id: "kgFoNa",
      });
    case CustomerServiceErrorCodes.StoreInLegacyMode:
      return intl.formatMessage({
        defaultMessage: "Internal server error. Please try again later",
        id: "XxYI2Z",
      });
    case CustomerServiceErrorCodes.StoreInModernMode:
      return intl.formatMessage({
        defaultMessage: "Internal server error. Please try again later",
        id: "XxYI2Z",
      });
    case CustomerServiceErrorCodes.StoreNotFound:
      return intl.formatMessage({
        defaultMessage: "Store not found",
        id: "ccTwMy",
      });
    case CustomerServiceErrorCodes.InvalidToken:
      return intl.formatMessage({
        defaultMessage:
          "Token is expired, Please verify your email or phone again",
        id: "Z1mpvB",
      });
    default:
      return intl.formatMessage({
        defaultMessage: "Oops, An Error occurred. please try again",
        id: "rPijV8",
      });
  }
}

export function getStoreAuthErrorMessageString(
  errorCode: any,
  intl: any
): string | null {
  switch (errorCode) {
    case StoreAuthServiceErrorCodes.NotFound:
      return intl.formatMessage({
        defaultMessage: "Resource not found",
        id: "e7zUWP",
      });
    case StoreAuthServiceErrorCodes.Invalid:
      return intl.formatMessage({
        defaultMessage: "Invalid request",
        id: "67sby6",
      });
    case StoreAuthServiceErrorCodes.Unauthorized:
      return intl.formatMessage({
        defaultMessage: "You are not authorized to perform this action",
        id: "yg1Vc0",
      });
    case StoreAuthServiceErrorCodes.InvalidInput:
      return intl.formatMessage({
        defaultMessage: "Invalid input provided",
        id: "n6d3tX",
      });
    case StoreAuthServiceErrorCodes.Forbidden:
      return intl.formatMessage({
        defaultMessage: "Access forbidden",
        id: "9pG+3p",
      });
    case StoreAuthServiceErrorCodes.Conflict:
      return intl.formatMessage({
        defaultMessage: "Request conflicts with current state",
        id: "I6vtkb",
      });
    case StoreAuthServiceErrorCodes.BadRequestError:
      return intl.formatMessage({
        defaultMessage: "Bad request. Please check your input",
        id: "t8hFPB",
      });
    case StoreAuthServiceErrorCodes.InternalServerError:
      return intl.formatMessage({
        defaultMessage: "Oops, An Error occurred. please try again",
        id: "rPijV8",
      });
    case StoreAuthServiceErrorCodes.InsufficientBalance:
      return intl.formatMessage({
        defaultMessage: "Please refresh your page",
        id: "tDh6Lc",
      });

    case StoreAuthServiceErrorCodes.OtpSendFailed:
      return intl.formatMessage({
        defaultMessage: "Failed to send OTP. Please try again",
        id: "xg1Le1",
      });
    case StoreAuthServiceErrorCodes.OtpNotFound:
      return intl.formatMessage({
        defaultMessage: "OTP not found or invalid",
        id: "uDs3pk",
      });
    case StoreAuthServiceErrorCodes.OtpExpired:
      return intl.formatMessage({
        defaultMessage: "OTP has expired. Please request a new one",
        id: "AjdnWc",
      });
    case StoreAuthServiceErrorCodes.UnsupportedAuthenticationProvider:
      return intl.formatMessage({
        defaultMessage: "Authentication provider not supported",
        id: "QZleyq",
      });
    case StoreAuthServiceErrorCodes.TooManyOtpRequests:
      return intl.formatMessage({
        defaultMessage:
          "Too many OTP requests. Please wait before trying again",
        id: "w7860w",
      });
    case StoreAuthServiceErrorCodes.SmsAuthenticationFailed:
      return intl.formatMessage({
        defaultMessage: "SMS authentication failed",
        id: "4vDd9a",
      });
    case StoreAuthServiceErrorCodes.SmsInvalidPhoneNumber:
      return intl.formatMessage({
        defaultMessage: "Invalid phone number for SMS",
        id: "vqFsKB",
      });
    case StoreAuthServiceErrorCodes.SmsInvalidSenderId:
      return intl.formatMessage({
        defaultMessage: "Invalid SMS sender ID",
        id: "N4Nqmu",
      });
    case StoreAuthServiceErrorCodes.SmsMessageTooLong:
      return intl.formatMessage({
        defaultMessage: "SMS message is too long",
        id: "Nk5urw",
      });
    case StoreAuthServiceErrorCodes.SmsRateLimitExceeded:
      return intl.formatMessage({
        defaultMessage: "SMS rate limit exceeded. Please try again later",
        id: "qNXPpe",
      });
    case StoreAuthServiceErrorCodes.SmsInsufficientBalance:
      return intl.formatMessage({
        defaultMessage: "Internal server error. Please try again later",
        id: "XxYI2Z",
      });
    case StoreAuthServiceErrorCodes.SmsNetworkError:
      return intl.formatMessage({
        defaultMessage: "SMS network error. Please try again",
        id: "6viAfC",
      });
    case StoreAuthServiceErrorCodes.SmsInternalError:
      return intl.formatMessage({
        defaultMessage: "SMS internal error. Please try again later",
        id: "K/zu2Z",
      });
    case StoreAuthServiceErrorCodes.SmsUnsupportedCountry:
      return intl.formatMessage({
        defaultMessage: "SMS not supported in this country",
        id: "Yi3D2L",
      });
    case StoreAuthServiceErrorCodes.EmailNotSent:
      return intl.formatMessage({
        defaultMessage: "Email could not be sent. Please try again",
        id: "PyEnI9",
      });
    case StoreAuthServiceErrorCodes.WhatsappInternalError:
      return intl.formatMessage({
        defaultMessage: "WhatsApp internal error. Please try again later",
        id: "HRWCd2",
      });
    case StoreAuthServiceErrorCodes.WrongOtp:
      return intl.formatMessage({
        defaultMessage: "Incorrect OTP. Please try again",
        id: "/Dk6AR",
      });
    case StoreAuthServiceErrorCodes.MismatchVerificationMethod:
      return intl.formatMessage({
        defaultMessage: "Verification method mismatch",
        id: "ezTe0a",
      });
    default:
      return null; // Return null instead of default message to allow chaining
  }
}
